/* Where an `InfoTip` bubble goes. Pure and separate from the component, so the rule is testable
 * without a layout engine — jsdom measures every element as 0x0 — and so the component file
 * exports components only. */

/* Distance from the icon, and the margin kept from every viewport edge. */
const GAP_PX = 6;
const EDGE_PX = 8;

export interface Placement {
  readonly top: number;
  readonly left: number;
  readonly side: 'top' | 'bottom';
}

/** Where the bubble goes: the preferred side unless it would leave the viewport and the other
 * side has more room, then centred on the icon and clamped horizontally.
 *
 * Pure, so the rule is testable without a layout engine — jsdom measures every element as 0x0. */
export function placeBubble(
  trigger: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'width'>,
  bubble: Pick<DOMRect, 'width' | 'height'>,
  viewport: { readonly width: number; readonly height: number },
  preferred: 'top' | 'bottom',
): Placement {
  const above = trigger.top - GAP_PX - EDGE_PX;
  const below = viewport.height - trigger.bottom - GAP_PX - EDGE_PX;
  const fitsPreferred = (preferred === 'top' ? above : below) >= bubble.height;
  const side: 'top' | 'bottom' = fitsPreferred
    ? preferred
    : preferred === 'top'
      ? below > above
        ? 'bottom'
        : 'top'
      : above > below
        ? 'top'
        : 'bottom';

  const top =
    side === 'top' ? trigger.top - GAP_PX - bubble.height : trigger.bottom + GAP_PX;
  const centred = trigger.left + trigger.width / 2 - bubble.width / 2;
  const maxLeft = Math.max(EDGE_PX, viewport.width - bubble.width - EDGE_PX);
  const left = Math.min(Math.max(centred, EDGE_PX), maxLeft);
  return { top: Math.max(EDGE_PX, top), left, side };
}
